import React, { Component } from 'react';
import { Accordion, Icon, Button } from 'semantic-ui-react';
import PropTypes from 'prop-types';

import { ItemEnum } from 'Common/FieldEnum';
import { ReactTablePropNameEnum } from 'Common/Constants';
import { getTotalMerchandiseAmount } from 'Pages/inventory/manageprocessorder/helper';
import {
  getPage,
  numericSortMethod,
  qtyOnHandSortMethod,
  dateSortMethod,
  caseInsensitiveFilter,
} from 'Common/GridHelper';

import { ItemGrid } from './ItemGrid';

class ItemGridWithCartContainer extends Component {
  static propTypes = {
    orderItemsTableName: PropTypes.string,
    orderItemsGridName: PropTypes.string,
    orderItems: PropTypes.object.isRequired,
    onOrderItemsChange: PropTypes.func.isRequired,
    onOrderItemRemove: PropTypes.func.isRequired,

    siteItemsTableName: PropTypes.string,
    siteItemsGridName: PropTypes.string,
    siteItems: PropTypes.array.isRequired,

    ...ItemGrid.propTypes,
  };

  static defaultProps = {};

  state = {
    accordionActive: [true, true],
    curPage: [],
    siteItemsPagingModel: {},
  };

  orderItemsRef = React.createRef();
  siteItemsRef = React.createRef();
  activeStyle = { color: '#df5a17', fontWeight: '600' };

  componentDidUpdate(prevProps) {
    const { siteItems } = this.props;

    //if we changed our list of site items, reset the site items grid to page 1
    if (siteItems !== prevProps.siteItems) {
      //all we have to do is set our page back to 1, and react table will call
      //onFetchData for us :)
      this.siteItemsRef.handleTableStateChange({
        [ReactTablePropNameEnum.page]: 0,
      });
    }
  }

  toggleAccordion = (index) => {
    const { accordionActive: oldActive } = this.state;
    const accordionActive = [...oldActive];
    accordionActive[index] = !accordionActive[index];
    this.setState({ accordionActive });
  };

  onFetchSiteItems = (pagingModel) => {
    const { siteItems, orderItems } = this.props;

    //apply any order item info on top of our site item info
    const appliedSiteItems = siteItems.map((item) => {
      const itemCode = item[ItemEnum.ItemCode];
      const orderItem = orderItems[itemCode];

      if (!orderItem) {
        return item;
      }

      return {
        ...item,
        [ItemEnum.PriQty]: orderItem[ItemEnum.PriQty],
        [ItemEnum.SecQty]: orderItem[ItemEnum.SecQty],
      };
    });

    //apply pagination with extra sort methods
    const curPage = getPage(appliedSiteItems, pagingModel, {
      [ItemEnum.PriQty]: numericSortMethod,
      [ItemEnum.SecQty]: numericSortMethod,
      [ItemEnum.PrimaryPrice]: numericSortMethod,
      [ItemEnum.QtyOnHand_Detail]: qtyOnHandSortMethod,
      [ItemEnum.BeginDate]: dateSortMethod,
      [ItemEnum.EndDate]: dateSortMethod,
    });

    this.setState({ curPage, siteItemsPagingModel: pagingModel });
  };

  handleClearFiltersClick = (ref) => {
    ref.handleTableStateChange({
      [ReactTablePropNameEnum.filtered]: [],
    });
  };

  getSiteItemsPages = () => {
    const { siteItems } = this.props;

    const {
      siteItemsPagingModel: {
        [ReactTablePropNameEnum.pageSize]: pageSize = 20,
        [ReactTablePropNameEnum.filtered]: filtered = [],
      },
    } = this.state;

    const filteredSiteItems = siteItems.filter((row) => {
      for (let i = 0; i < filtered.length; i++) {
        if (!caseInsensitiveFilter(filtered[i], row)) {
          return false;
        }
      }

      return true;
    });

    const pages = Math.ceil(filteredSiteItems.length / pageSize);
    return pages;
  };

  getTrProps = (state, { original }) => {
    const { disabled, orderItems } = this.props;

    if (!disabled && original && orderItems[original[ItemEnum.ItemCode]]) {
      return { style: this.activeStyle };
    }

    return {};
  };

  render() {
    const {
      orderItemsTableName,
      orderItemsGridName,
      orderItems,
      onOrderItemsChange,
      onOrderItemRemove,

      siteItemsTableName,
      siteItemsGridName,
      siteItems,

      disabled,
      loading,
      ...tableProps
    } = this.props;

    const { accordionActive, curPage } = this.state;

    const orderItemsArray = Object.values(orderItems);
    const orderItemsTotal = getTotalMerchandiseAmount(orderItemsArray);

    //make sure most recent items are on top
    orderItemsArray.sort(
      (itemA, itemB) => numericSortMethod(itemA._added, itemB._added) * -1
    );

    return (
      <React.Fragment>
        <Accordion fluid styled style={{ marginBottom: '2rem' }}>
          <Accordion.Title
            active={accordionActive[0]}
            onClick={() => this.toggleAccordion(0)}
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <div>
              <Icon name="dropdown" />
              <span className="text-orange">Order Items</span>
            </div>
            <div className="text-orange">
              Total merchandise amount: $ {orderItemsTotal}
            </div>
          </Accordion.Title>
          <Accordion.Content active={accordionActive[0]}>
            <Button
              className={'clear-filters-button'}
              content="Clear Filters"
              onClick={() => this.handleClearFiltersClick(this.orderItemsRef)}
            />
            <ItemGrid
              {...tableProps}
              tableName={orderItemsTableName}
              gridName={orderItemsGridName}
              data={orderItemsArray}
              onRef={(ref) => {
                this.orderItemsRef = ref;
              }}
              orderItems={orderItems}
              searchItems={siteItems}
              searchColumns={[ItemEnum.ItemCode, ItemEnum.Description]}
              onSearchSubmit={onOrderItemsChange}
              onQuantityChange={onOrderItemsChange}
              onItemRemove={onOrderItemRemove}
              getTrProps={() => ({
                style: this.activeStyle,
              })}
              showRemoveButton={true}
              disabled={disabled || loading || !(siteItems && siteItems.length)}
              loading={loading}
            />
          </Accordion.Content>
        </Accordion>
        <Accordion fluid styled>
          <Accordion.Title
            active={accordionActive[1]}
            onClick={() => this.toggleAccordion(1)}
          >
            <Icon name="dropdown" />
            <span className="text-orange">Browse Items</span>
          </Accordion.Title>
          <Accordion.Content active={accordionActive[1]}>
            <Button
              className={'clear-filters-button'}
              content="Clear Filters"
              onClick={() => this.handleClearFiltersClick(this.siteItemsRef)}
            />
            <ItemGrid
              {...tableProps}
              tableName={siteItemsTableName}
              gridName={siteItemsGridName}
              manual
              data={curPage}
              onRef={(ref) => {
                this.siteItemsRef = ref;
              }}
              pages={this.getSiteItemsPages()}
              onFetchData={this.onFetchSiteItems}
              orderItems={orderItems}
              onQuantityChange={onOrderItemsChange}
              onItemRemove={onOrderItemRemove}
              getTrProps={this.getTrProps}
              showRemoveButton={false}
              disabled={disabled || loading}
              loading={loading}
            />
          </Accordion.Content>
        </Accordion>
      </React.Fragment>
    );
  }
}

export const ItemGridWithCart = ItemGridWithCartContainer;



// WEBPACK FOOTER //
// ./src/components/itemgridformfield/ItemGridWithCart.js